import React, { useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { useAuth } from '../../features/auth/context/useAuth';
import { useTranslation } from '../../i18n';
import { Header } from './Header';
import { Sidebar } from './Sidebar';

interface LayoutProps {
	children: ReactNode;
	showSidebar?: boolean;
}

export const Layout: React.FC<LayoutProps> = ({ children, showSidebar = true }) => {
	const { t } = useTranslation();
	const { session, loading: isLoading } = useAuth();
	const isAuthenticated = !!session;
	const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

	const toggleMobileSidebar = () => {
		setIsMobileSidebarOpen(!isMobileSidebarOpen);
	};

	const closeMobileSidebar = () => {
		setIsMobileSidebarOpen(false);
	};

	// Prevent body scroll when mobile sidebar is open
	useEffect(() => {
		if (isMobileSidebarOpen) {
			document.body.style.overflow = 'hidden';
		} else {
			document.body.style.overflow = 'unset';
		}

		return () => {
			document.body.style.overflow = 'unset';
		};
	}, [isMobileSidebarOpen]);

	// Show loading state while authentication is being determined
	if (isLoading) {
		if (process.env.NODE_ENV === 'development') {
			console.log('⏳ Layout: Authentication state loading...');
		}
		return (
			<div className='min-h-screen bg-gray-50 flex items-center justify-center'>
				<div className='text-center'>
					<div className='animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4'></div>
					<p className='text-gray-600'>{t('common.loading')}</p>
				</div>
			</div>
		);
	}

	// Don't show layout for auth pages or guests
	if (!isAuthenticated) {
		if (process.env.NODE_ENV === 'development') {
			console.log('🚫 Layout: Not showing layout - not authenticated or guest role');
		}
		return <div className='min-h-screen bg-gray-50'>{children}</div>;
	}

	if (process.env.NODE_ENV === 'development') {
		console.log('✅ Layout: Showing full layout with sidebar');
	}

	return (
		<div className='min-h-screen bg-gray-50'>
			<Header onMobileMenuToggle={toggleMobileSidebar} />
			<div className='flex relative'>
				{showSidebar && <Sidebar isMobileOpen={isMobileSidebarOpen} onMobileClose={closeMobileSidebar} />}
				<main
					className={`flex-1 ${showSidebar ? 'md:ml-16 lg:ml-64' : ''} transition-all duration-300 relative`}
					onClick={() => {
						// Close mobile sidebar when clicking on main content
						if (isMobileSidebarOpen) {
							closeMobileSidebar();
						}
					}}
				>
					<div className='container mx-auto px-4 py-6 max-w-7xl w-full'>{children}</div>
				</main>
			</div>
		</div>
	);
};
